import { create } from "zustand";
import type { ToastType } from "../app/components/ui/Toast";

export type AlertSeverity = "critical" | "error" | "warning" | "info" | "success";

interface Toast {
  id: string;
  type: ToastType;
  severity?: AlertSeverity; // New: severity level for hierarchy
  title: string;
  message: string;
  duration?: number;
  errorCode?: string; // For deduplication by code instead of message
  category?: string; // For grouping related alerts
}

interface ToastStore {
  queue: Toast[];
  currentToast: Toast | null;
  lastErrorTime: number;
  lastErrorMessage: string;
  deduplicationMap: Record<string, number>; // Track error codes by time
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: () => void;
  clearToasts: () => void;
  showSuccess: (title: string, message: string, duration?: number) => void;
  showError: (title: string, message: string, duration?: number) => void;
  showCritical: (title: string, message: string, duration?: number) => void;
  showWarning: (title: string, message: string, duration?: number) => void;
  showInfo: (title: string, message: string, duration?: number) => void;
  showByCode: (code: string, title: string, message: string, severity?: AlertSeverity, duration?: number) => void;
}

// Default durations by severity
const SEVERITY_DURATIONS: Record<AlertSeverity, number> = {
  critical: 0, // Persist until manually closed
  error: 6000,
  warning: 5000,
  info: 4000,
  success: 3000,
};

const createToast = (
  type: ToastType,
  title: string,
  message: string,
  duration = 5000,
  severity?: AlertSeverity,
  errorCode?: string,
  category?: string
): Toast => ({
  id: `toast-${Date.now()}-${Math.random()}`,
  type,
  severity,
  title,
  message,
  duration,
  errorCode,
  category,
});

/**
 * Map severity to toast type for visual rendering
 */
const severityToToastType = (severity: AlertSeverity): ToastType => {
  switch (severity) {
    case "critical":
      return "critical";
    case "error":
      return "warning";
    case "warning":
      return "warning";
    case "info":
      return "success"; // Reuse success styling for info
    case "success":
      return "success";
  }
};

export const useToastStore = create<ToastStore>((set, get) => ({
  queue: [],
  currentToast: null,
  lastErrorTime: 0,
  lastErrorMessage: "",
  deduplicationMap: {},

  addToast: (toast) => {
    const newToast = createToast(
      toast.type,
      toast.title,
      toast.message,
      toast.duration,
      toast.severity,
      toast.errorCode,
      toast.category
    );
    const state = get();

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },

  removeToast: () => {
    const state = get();
    if (state.queue.length > 0) {
      set({
        currentToast: state.queue[0],
        queue: state.queue.slice(1),
      });
    } else {
      set({ currentToast: null });
    }
  },

  clearToasts: () => {
    set({ queue: [], currentToast: null, deduplicationMap: {} });
  },

  showSuccess: (title, message, duration) => {
    const finalDuration = duration ?? SEVERITY_DURATIONS.success;
    const newToast = createToast("success", title, message, finalDuration, "success");
    const state = get();

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },

  showError: (title, message, duration) => {
    const state = get();
    const now = Date.now();
    const errorKey = `${title}:${message}`;

    // Deduplicate errors within 2 second window
    if (state.lastErrorMessage === errorKey && now - state.lastErrorTime < 2000) {
      return;
    }

    const finalDuration = duration ?? SEVERITY_DURATIONS.error;
    const newToast = createToast("warning", title, message, finalDuration, "error");

    set({
      lastErrorTime: now,
      lastErrorMessage: errorKey,
    });

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },

  showCritical: (title, message, duration) => {
    const state = get();
    const now = Date.now();
    const errorKey = `${title}:${message}`;

    // Critical alerts always show (no deduplication)
    const finalDuration = duration ?? SEVERITY_DURATIONS.critical;
    const newToast = createToast("critical", title, message, finalDuration, "critical");

    set({
      lastErrorTime: now,
      lastErrorMessage: errorKey,
    });

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },

  showWarning: (title, message, duration) => {
    const state = get();
    const now = Date.now();
    const errorKey = `${title}:${message}`;

    // Deduplicate warnings within 2 second window
    if (state.lastErrorMessage === errorKey && now - state.lastErrorTime < 2000) {
      return;
    }

    const finalDuration = duration ?? SEVERITY_DURATIONS.warning;
    const newToast = createToast("warning", title, message, finalDuration, "warning");

    set({
      lastErrorTime: now,
      lastErrorMessage: errorKey,
    });

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },

  showInfo: (title, message, duration) => {
    const finalDuration = duration ?? SEVERITY_DURATIONS.info;
    const newToast = createToast("success", title, message, finalDuration, "info");
    const state = get();

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },

  showByCode: (code, title, message, severity = "error", duration) => {
    const state = get();
    const now = Date.now();
    const dedupeWindow = 2000;

    // Check if this error code was recently shown
    const lastShown = state.deduplicationMap[code];
    if (lastShown && now - lastShown < dedupeWindow) {
      // Skip critical alerts from deduplication, but allow others
      if (severity !== "critical") {
        return;
      }
    }

    const finalDuration = duration ?? SEVERITY_DURATIONS[severity];
    const toastType = severityToToastType(severity);
    const newToast = createToast(toastType, title, message, finalDuration, severity, code);

    // Update deduplication map
    const newDedupeMap = { ...state.deduplicationMap, [code]: now };
    set({ deduplicationMap: newDedupeMap });

    if (state.currentToast === null) {
      set({ currentToast: newToast });
    } else {
      set({ queue: [...state.queue, newToast] });
    }
  },
}));
